<template>
    <div>
        <!--Edit Training Dialog-->
        <Dialog :visible.sync="trainingDialog" :style="{width: '450px'}" header="Training Details"
                :modal="true" :maximizable="true" class="p-fluid">

            <div class="p-field" v-if="createTraining">
                <label for="staff">Staff</label>
                <Dropdown id="staff" v-model="training.staff"
                          :options="staffs" optionLabel="name" required="true" :filter="true"
                          :class="{'p-invalid': submitted && $v.training.staff.$invalid}"/>
                <small class="p-invalid" v-if="submitted && $v.training.staff.$invalid">Staff is
                    required.</small>
            </div>

            <div class="p-field">
                <label for="category">Category</label>
                <Dropdown id="category" v-model="training.category" :options="categories" optionLabel="name"
                          optionValue="name" required="true" :filter="true"
                          :class="{'p-invalid': submitted && $v.training.name.$invalid}"/>
                <small class="p-invalid" v-if="submitted && $v.training.name.$invalid">Category is
                    required.</small>
            </div>

            <div class="p-field">
                <label for="name">Name</label>
                <InputText id="name" v-model.trim="training.name" required="true"
                           :class="{'p-invalid': submitted && $v.training.name.$invalid}"/>
                <small class="p-invalid" v-if="submitted && $v.training.name.$invalid">Name is
                    required.</small>
            </div>

            <div class="p-field">
                <label for="description">Description</label>
                <InputText id="description" v-model.trim="training.description" required="true" autofocus/>
            </div>

            <div class="p-field">
                <label for="organizer">Organizer</label>
                <InputText id="organizer" v-model.trim="training.organizer" required="true" autofocus
                           :class="{'p-invalid': submitted && $v.training.organizer.$invalid}"/>
                <small class="p-invalid" v-if="submitted && $v.training.organizer.$invalid">Organizer is
                    required.</small>
            </div>

            <div class="p-field">
                <label for="date">Awarded Month (MM/YYYY)</label>
                <Calendar id="date" v-model="training.date" view="month" dateFormat="mm/yy"
                          :yearNavigator="true" :yearRange="`1950:${new Date().getFullYear()}`" placeholder="MM/YYYY"
                          :class="{'p-invalid': submitted && $v.training.date.$invalid}"/>
                <small class="p-invalid" style="margin-right: 1em" v-if="submitted && !$v.training.date.required">
                    Date is required.
                </small>
                <small class="p-invalid" v-if="submitted && !$v.training.date.dateFormat">
                    Date is in wrong format.
                </small>
            </div>

            <template #footer>
                <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="hideDialog"/>
                <Button label="Save" icon="pi pi-check" class="p-button-text" @click="saveTraining"/>
            </template>
        </Dialog>


        <!--Delete Training Dialog-->
        <Dialog :visible.sync="deleteTrainingDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem"/>
                <span v-if="training">Are you sure you want to delete <b>{{ training.name }}</b>?</span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text"
                        @click="deleteTrainingDialog = false"/>
                <Button label="Yes" icon="pi pi-check" class="p-button-text" @click="deleteTraining"/>
            </template>
        </Dialog>

        <!--Delete Trainings Dialog-->
        <Dialog :visible.sync="deleteTrainingsDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem"/>
                <span v-if="training">Are you sure you want to delete the selected trainings?</span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text"
                        @click="deleteTrainingsDialog = false"/>
                <Button label="Yes" icon="pi pi-check" class="p-button-text"
                        @click="deleteSelectedTrainings"/>
            </template>
        </Dialog>

        <!--Verify Trainings Dialog-->
        <Dialog :visible.sync="toggleTrainingsDialog" :style="{width: '450px'}" header="Confirm" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem"/>
                <span v-if="training">Are you sure you want to {{ verifyTrainings ? 'verify' : 'unverify' }} the selected training?</span>
            </div>
            <template #footer>
                <Button label="No" icon="pi pi-times" class="p-button-text"
                        @click="toggleTrainingsDialog = false"/>
                <Button label="Yes" icon="pi pi-check" class="p-button-text"
                        @click="toggleSelectedTrainings"/>
            </template>
        </Dialog>
    </div>
</template>

<script>
import {Component, Prop, Vue, Watch} from "vue-property-decorator";

Component.registerHooks(['validations']);
import {required} from 'vuelidate/lib/validators'
import Toast from 'primevue/toast';
import Dialog from "primevue/dialog";
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import InputSwitch from "primevue/inputswitch";
import Calendar from 'primevue/calendar';
import TrainingService from "@/service/TrainingService";
import StaffService from "@/service/StaffService";
import store from "../../store";

/**
 * Class-component
 */
@Component({
    components: {
        Toast: Vue.extend(Toast),
        Dialog: Vue.extend(Dialog),
        Dropdown: Vue.extend(Dropdown),
        InputText: Vue.extend(InputText),
        Button: Vue.extend(Button),
        InputSwitch: Vue.extend(InputSwitch),
        Calendar: Vue.extend(Calendar)
    }
})
export default class TrainingDialog extends Vue {
    createTraining = false;
    staffService = new StaffService();
    staffs = [];
    trainingService = new TrainingService();
    categories = this.trainingService.availableCategories();
    trainingDialog = false;
    deleteTrainingDialog = false;
    deleteTrainingsDialog = false;
    toggleTrainingsDialog = false;
    verifyTrainings = false;
    training = {};
    submitted = false;
    selectedTrainings = [];
    toastSuccess = {
        severity: 'success',
        summary: 'Successful',
        detail: '',
        life: 3000
    };
    toastError = {
        severity: 'error',
        summary: 'Error',
        detail: '',
        life: 3000
    };

    validations() {
        const dateFormat = value => {
            if (typeof value === 'string' && !value.match(/^\d{1,2}\/\d{4}$/g)) return false
            return this.$moment(value, "MM/YYYY").isSameOrBefore(this.$moment())
        }
        return {
            training: {
                staff: {required},
                category: {required},
                name: {required},
                organizer: {required},
                date: {required, dateFormat}
            }
        }
    }

    @Watch('createTraining')
    async onCreateTrainingChanged() {
        if (this.createTraining) this.staffs = await this.staffService.getStaffs();
    }

    @Watch('training')
    async onTrainingChanged() {
        if (this.training.date) this.training.date = this.$formatDate(this.training.date)
    }

    hideDialog() {
        this.trainingDialog = false;
        this.submitted = false;
    }

    async saveTraining() {
        this.submitted = true;

        if (this.$v.$invalid) {
            return; // invalid input
        }

        const trainingObj = {
            'verified': 0,
            _jv: {
                type: 'trainings',
                relationships: {
                    staff: {data: {id: this.training.staff._jv.id, type: 'staffs'}}
                }
            }
        };

        ['category', 'name', 'description', 'organizer'].forEach(el => {
            trainingObj[el] = this.training[el]
        });

        trainingObj['date'] = this.$moment(this.training.date, "MM/YYYY").toISOString()

        try {
            if (this.training._jv) {
                // PATCH
                trainingObj._jv.id = this.training._jv.id
                await this.trainingService.updateTraining(trainingObj)
            } else {
                // POST
                await this.trainingService.createTraining(trainingObj);
            }
            this.$emit('updated')
            this.toastSuccess.detail = 'Trainings Updated'
            this.$toast.add(this.toastSuccess);
        } catch (e) {
            this.toastError.detail = e.message
            this.$toast.add(this.toastError);
            this.trainingService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err
                });
            })
        }

        this.trainingDialog = false;
        this.training = {};
    }

    async deleteTraining() {
        try {
            await this.trainingService.deleteTraining(this.training._jv.id)
            this.$emit('updated')
            this.toastSuccess.detail = 'Training Deleted'
            this.$toast.add(this.toastSuccess);
        } catch (e) {
            this.toastError.detail = e.message
            this.$toast.add(this.toastError);
            this.trainingService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err
                });
            })
        }

        this.deleteTrainingDialog = false;
        this.training = {};
    }

    async deleteSelectedTrainings() {
        try {
            for (const deleteTraining of this.selectedTrainings) {
                await this.trainingService.deleteTraining(deleteTraining._jv.id)
            }
            this.$emit('updated')
            this.toastSuccess.detail = 'Trainings Deleted'
            this.$toast.add(this.toastSuccess);
        } catch (e) {
            this.toastError.detail = e.message
            this.$toast.add(this.toastError);
            this.trainingService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err
                });
            })
        }
        this.deleteTrainingsDialog = false;
        this.selectedTrainings = [];
    }

    async toggleSelectedTrainings() {
        try {
            if (this.verifyTrainings) {
                // verify
                for (const verifyTraining of this.selectedTrainings) {
                    const trainingObj = {
                        'verified': 1,
                        'verified-by': store.state.staffId,
                        'verified-at': this.$moment().toISOString(),
                        _jv: {
                            id: verifyTraining._jv.id,
                            type: 'trainings',
                            relationships: {
                                staff: {data: {id: verifyTraining.staff._jv.id, type: 'staffs'}}
                            }
                        }
                    };
                    await this.trainingService.updateTraining(trainingObj)
                }
            }

            if (!this.verifyTrainings) {
                // unverify
                for (const verifyTraining of this.selectedTrainings) {
                    const trainingObj = {
                        'verified': 0,
                        'verified-by': null,
                        'verified-at': null,
                        _jv: {
                            id: verifyTraining._jv.id,
                            type: 'trainings',
                            relationships: {
                                staff: {data: {id: verifyTraining.staff._jv.id, type: 'staffs'}}
                            }
                        }
                    };
                    await this.trainingService.updateTraining(trainingObj)
                }
            }

            this.$emit('updated')
            this.toastSuccess.detail = 'Trainings Updated'
            this.$toast.add(this.toastSuccess);
        } catch (e) {
            this.toastError.detail = e.message
            this.$toast.add(this.toastError);
            this.trainingService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err,
                });
            })
        }

        this.toggleTrainingsDialog = false;
        this.selectedTrainings = [];
    }
}


</script>

<style scoped>

</style>
